import {
  getPathFromState as originalGetPathFromState,
  getStateFromPath as originalGetStateFromPath,
  LinkingOptions,
} from '@react-navigation/native';

import {
  MAIN_SCREENS_ROUTES,
  REPORT_ROUTE_MAP,
  REVERSE_REPORT_ROUTE_MAP,
} from '../constant/web-navigation.constant';

/**
 * Web-specific linking configuration for React Navigation.
 * This configuration handles clean URL mapping and state re-hydration for the web platform.
 */

/**
 * THE MOTO:
 * This file is the "Translator" between the App's internal brain and the Browser's address bar.
 *
 * Why do we need this?
 * 1. Clean URLs: Internally, the app uses complex names like 'REPORT_ALL_GEN0_CLIENTS'.
 *    We want the user to see '/dashboard/all-gen0-clients'.
 * 2. Deep Linking: If someone types a URL or refreshes the page, we need to
 *    reconstruct the app state so they land exactly where they were, with the
 *    correct background screens visible.
 */

// Basic setup to know if we are on http or https and what the domain is (e.g., localhost:3000)
const currentProtocol =
  typeof window !== 'undefined' ? window.location.protocol : 'http:';
const currentHost =
  typeof window !== 'undefined' ? window.location.host : 'localhost:3000';
const dynamicPrefixes = [`${currentProtocol}//${currentHost}`];

export const webLinking: LinkingOptions<{}> = {
  prefixes: dynamicPrefixes,
  /**
   * config: This is the "Map" of the app. It tells React Navigation
   * which internal screen names correspond to which URL paths.
   */
  config: {
    screens: {
      // The main container that can show pop-up "modals" on top of everything.
      'content-with-modal': {
        path: '',
        screens: {
          // The container for our main tabs (Dashboard, Clients, etc.)
          'content-with-card': {
            path: '',
            screens: {
              // Main Tab Screens: The primary navigation tabs (Dashboard, Clients, etc.)
              ...MAIN_SCREENS_ROUTES,
            },
          },

          // The internal "Card" system used for showing reports/profiles.
          card: {
            path: ':mainPath/:modalName',
            screens: {
              'records-main-screen': '',
              'records-record-screen': {
                path: 'record/:recordType',
              },
              'myteams-data': 'myteams-data',
            },
          },
        },
      },
      // Standard pop-up modals.
      modal: {
        path: 'modal/:modalName',
        screens: {
          'records-main-screen': '',
          'records-record-screen': {
            path: 'record/:recordType',
          },
        },
      },
    },
  },

  /**
   * getPathFromState: The "App -> URL" Translator.
   * Logic: "The app just moved to a new screen. What should I show in the address bar?"
   */
  getPathFromState(state, options) {
    // console.log('[nav-1]: Entering getPathFromState', {state});
    /**
     * Helper: Looks deep into the app state to find if a "Report" or "Profile" is open.
     */
    const findModalParams = (
      s: any,
    ): {modalName?: string; mainPath?: string} | null => {
      if (!s?.routes) return null;

      // Search from top to bottom (end of array)
      for (let i = s.routes.length - 1; i >= 0; i--) {
        const route = s.routes[i];

        // Deep-first search: Prioritize leaf nodes (nested modals/records)
        if (route.state) {
          const nested = findModalParams(route.state);
          if (nested) return nested;
        }
        // Check if this route explicitly defines a modal name
        if (
          route.params?.modalName &&
          route.params.modalName !== 'ORDER_DETAILS'
        ) {
          return {
            modalName: route.params.modalName,
            mainPath: route.params.mainPath,
            // shadowId: route.params.shadowId,
          };
        }

        // Handle record-specific screens which might not have 'modalName' but have 'recordType'
        if (route.params?.recordType) {
          return {
            modalName: route.params.recordType,
            mainPath: route.params.mainPath,
            // Use recordId as the primary identifier (internally)
            // shadowId: route.params.recordId || route.params.shadowId,
          };
        }
      }
      return null;
    };

    /**
     * Helper: Figures out which main tab (e.g., Dashboard, Clients) is active in the background.
     */
    const findMainScreenPath = (s: any): string => {
      if (!s?.routes) return '';

      // 1. Check if ANY route at this level is the main content navigator
      const contentWithCard = s.routes.find(
        (r: any) => r.name === 'content-with-card',
      );

      if (contentWithCard && contentWithCard.state) {
        const activeMainRoute =
          contentWithCard.state.routes[contentWithCard.state.index];
        if (activeMainRoute) {
          const config = (MAIN_SCREENS_ROUTES as any)[activeMainRoute.name];
          return typeof config === 'string' ? config : config?.path || '';
        }
      }

      // If not found at this level, recurse into the active route's state
      // (This handles cases where the state is still at the root level)
      const activeRoute = s.routes[s.index];
      if (activeRoute && activeRoute.state) {
        return findMainScreenPath(activeRoute.state);
      }

      return '';
    };

    const modalParams = findModalParams(state);
    const mainScreenPath = modalParams?.mainPath || findMainScreenPath(state);
    // console.log('[nav-2]: modalParams and mainScreenPath found', {
    //   modalParams,
    //   mainScreenPath,
    // });
    let finalPath = '';

    // Step 1: Find the "Clean" name for the report (e.g., 'all-gen0-clients')
    let reportPath: any = '';
    if (modalParams?.modalName) {
      if (REPORT_ROUTE_MAP[modalParams.modalName]) {
        // modalName is the internal key (e.g., REPORT_ALL_GEN0_CLIENTS)
        reportPath = REPORT_ROUTE_MAP[modalParams.modalName];
      } else {
        // Fallback: check if modalName is already the clean value (e.g., all-gen0-clients)
        const cleanValues = Object.values(REPORT_ROUTE_MAP);
        if (cleanValues.includes(modalParams.modalName)) {
          reportPath = modalParams.modalName;
        }
      }
    }
    // console.log('[nav-3]: Resolved reportPath', {reportPath});

    // Step 2: Combine the Main Tab + Report name (e.g., '/dashboard/all-gen0-clients')
    if (reportPath) {
      /**
       * Nest the report path under the active main screen path if available.
       * Requirement: localhost:3000/MAIN_SCREENS_ROUTE/REPORT_ROUTE_MAP
       * e.g., /dashboard/all-frontline-clients instead of just /all-frontline-clients
       */
      finalPath =
        mainScreenPath && mainScreenPath !== reportPath
          ? `${mainScreenPath}/${reportPath}`
          : reportPath;
    } else {
      // If it's not a special report, just let the standard system handle the URL
      const path = originalGetPathFromState(state, options);
      // Strip query parameters to keep the URL clean
      finalPath = path ? path.split('?')[0] : '';
    }
    // console.log('[nav-4]: Final path before normalization', {finalPath});

    /**
     * Ensure we always return an absolute path (starting with /).
     * Prepending '/' forces the browser to resolve from the domain root.
     */
    if (finalPath && !finalPath.startsWith('/')) {
      finalPath = `/${finalPath}`;
    } else if (!finalPath) {
      finalPath = '/';
    }

    // console.log('[nav-5]: Returning finalPath', {finalPath});
    return finalPath;
  },

  /**
   * getStateFromPath: The "URL -> App" Translator.
   * Logic: "The user just typed a URL. How do I set up the app to match it?"
   */
  getStateFromPath(path, options) {
    // console.log('[nav-6]: Entering getStateFromPath', {path});
    // Normalize path by removing leading slash and query params
    let internalPath: any = path.split('?')[0];

    // --- LEGACY CLEANUP (Optional to keep) ---
    // This cleans up old, ugly URLs that might still be in people's bookmarks.
    if (internalPath.includes('/content-with-modal/content-with-card/')) {
      internalPath = internalPath.replace(
        '/content-with-modal/content-with-card/',
        '/',
      );
    }
    if (
      internalPath === '/content-with-modal' ||
      internalPath === 'content-with-modal'
    ) {
      internalPath = '/';
    }
    // ------------------------------------------

    // Extract the segments to identify main screen and report
    const normalizedPath = internalPath.startsWith('/')
      ? internalPath.substring(1)
      : internalPath;

    const pathParts = normalizedPath.split('/');
    let mainPath = '';
    let reportSegment = '';

    /**
     * Step 1: Break the URL down.
     * If URL is '/dashboard/all-gen0-clients', then:
     * mainPath = 'dashboard'
     * reportSegment = 'all-gen0-clients'
     */
    if (pathParts.length >= 2 && REVERSE_REPORT_ROUTE_MAP[pathParts[1]]) {
      mainPath = pathParts[0];
      reportSegment = pathParts[1];
    } else if (
      REVERSE_REPORT_ROUTE_MAP[pathParts[0]] &&
      pathParts[0] !== 'my-teams'
    ) {
      // Fallback for non-nested report paths (legacy or direct entry)
      reportSegment = pathParts[0];
    }
    // console.log('[nav-7]: URL Breakdown', {mainPath, reportSegment});

    // Step 2: Tell the app to open the "Card" (report/profile) internally
    if (reportSegment) {
      const modalName = REVERSE_REPORT_ROUTE_MAP[reportSegment];
      // Translate to the internal nested structure defined in 'config'
      // We explicitly pass mainPath as a param so it can be recovered in getPathFromState
      internalPath = `/${mainPath}/${modalName}?mainPath=${mainPath}`;
    }
    // console.log('[nav-8]: Translated internalPath', {internalPath});

    // Use original logic to generate the initial state tree from our translated path
    const state = originalGetStateFromPath(internalPath, options);
    if (!state) {
      // console.log('[nav-9]: getStateFromPath returned null');
      return state;
    }

    /**
     * Helper function to find a route by name in a state tree.
     */
    const findRouteByName = (s: any, name: string): any => {
      if (!s?.routes) return null;
      for (const r of s.routes) {
        if (r.name === name) return r;
        if (r.state) {
          const found = findRouteByName(r.state, name);
          if (found) return found;
        }
      }
      return null;
    };

    /**
     * Step 3: "Re-hydration" (The Magic Step).
     * If a user goes directly to '/dashboard/report', we don't want them to see
     * just the report on a blank screen. We need to "Inject" the Dashboard
     * background behind the report pop-up so it feels like they are still in the app.
     */
    if (mainPath && reportSegment) {
      // console.log('[nav-10]: Starting re-hydration for mainPath', {mainPath});
      const contentWithModalRoute = findRouteByName(
        state,
        'content-with-modal',
      );

      if (contentWithModalRoute && contentWithModalRoute.state) {
        // Find what the background tabs should look like for 'mainPath' (e.g., 'dashboard')
        const mainState = originalGetStateFromPath(`/${mainPath}`, options);
        const mainContentWithCardRoute = findRouteByName(
          mainState,
          'content-with-card',
        );

        if (mainContentWithCardRoute) {
          const mRoutes = contentWithModalRoute.state.routes;
          // Ensure the background tab navigator is present in the stack
          const existingIdx = mRoutes.findIndex(
            (r: any) => r.name === 'content-with-card',
          );

          // Put the background tab state into our current app state
          if (existingIdx !== -1) {
            mRoutes[existingIdx] = mainContentWithCardRoute;
          } else {
            mRoutes.unshift(mainContentWithCardRoute);
          }
          // Ensure the card (the actual report) remains the active route in the stack
          contentWithModalRoute.state.index = mRoutes.length - 1;
          // console.log('[nav-11]: Re-hydration complete');
        }
      }
    }

    // console.log('[nav-12]: Final state returned', {state});
    return state;
  },
};
